import type { QueryClient } from '@tanstack/react-query';
import { getDispatchJob, type DispatchJobStatus } from '@/lib/api/routes/visits';

/**
 * Visit lists that a dispatch adds a row to. The customer page keeps its visit
 * list on `['customerVisits', …]` and each service's history / next-visit line
 * on `['subscriptionVisits', …]`; neither is refreshed by the subscription or
 * customer-detail invalidations, so a fresh dispatch stayed invisible until a
 * reload (RUT-4441, a regression of RUT-2693).
 */
export const DISPATCHED_VISIT_QUERY_KEYS = [['customerVisits'], ['subscriptionVisits']] as const;

export function invalidateDispatchedVisits(queryClient: QueryClient) {
  return Promise.all(
    DISPATCHED_VISIT_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

const SETTLED: ReadonlySet<DispatchJobStatus> = new Set([
  'completed',
  'partial_failure',
  'failed',
  'cancelled',
]);

/** Short: the page should show the visit as soon as the processor writes it. */
export const DISPATCH_SETTLE_POLL_MS = 500;
/** About 30 s of polling, enough for a one-customer dispatch; the websocket covers the rest. */
export const DISPATCH_SETTLE_MAX_POLLS = 60;

/**
 * A new dispatch (`POST /visits {dispatch}`) answers with a job id and creates
 * the visits in the background, so refreshing right away refetches before they
 * exist. Refresh now, then poll the job and refresh again whenever it has
 * created more visits and once it has settled. The `DISPATCH_JOBS` websocket
 * event does the same when the socket is up; this does not depend on it
 * (RUT-4441: QA's socket closed 1006 and the page never updated).
 */
export async function refreshVisitsWhenDispatchSettles(
  queryClient: QueryClient,
  jobId: string | undefined,
  opts: { pollMs?: number; maxPolls?: number } = {},
): Promise<void> {
  // Not awaited: the refetch can take a second, and the job may be done by then.
  const first = invalidateDispatchedVisits(queryClient);
  if (!jobId) {
    await first;
    return;
  }
  const pollMs = opts.pollMs ?? DISPATCH_SETTLE_POLL_MS;
  const maxPolls = opts.maxPolls ?? DISPATCH_SETTLE_MAX_POLLS;
  let seen = 0;
  for (let i = 0; i < maxPolls; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, pollMs));
    let job: Awaited<ReturnType<typeof getDispatchJob>>['job'];
    try {
      ({ job } = await getDispatchJob(jobId));
    } catch {
      return; // the websocket event, or the next reload, still refreshes
    }
    const created = job.createdCount ?? 0;
    const settled = SETTLED.has(job.status);
    if (created > seen || settled) {
      seen = created;
      await invalidateDispatchedVisits(queryClient);
    }
    if (settled) return;
  }
}
